import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Chip, InformationBox, LabeledSection } from '@app/core';
import {
  useGenerateReferralCode,
  useReferralCode,
  useSponsoredChild,
} from '@app/api';
import { useCurrentOrganizationId } from 'queries';
import { SharableLink } from '@app/form';
import { SettingContent } from './components/SettingContent';

const useStyles = createUseStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.marginBase * 3,
  },
  title: {
    ...theme.fonts.h2,
  },
  referralCodeContainer: {
    marginTop: theme.marginBase * 3,
  },
  link: {
    width: '100%',
  },
  sponsoredList: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3 + 'px',
    width: '100%',
  },
  memberListItemWithSeparator: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3 + 'px',
  },
  invitedMembersList: {
    marginTop: theme.marginBase * 3,
  },
  separator: {
    height: 1,
    width: '100%',
    backgroundColor: theme.colors.grayBorder,
  },
  sponsoredListItem: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: theme.colors.inkSecondary,
  },
  sponsoredEmail: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
  },
  sponsoredTitle: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 3,
  },
  sponsoredContainer: {
    marginTop: theme.marginBase * 6,
  },
  sponsoredListItemFirst: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.marginBase * 2,
  },
  date: {
    ...theme.fonts.caption,
  },
}));

export const Referrals = () => {
  const classes = useStyles();
  const organizationId = useCurrentOrganizationId();
  const { data: referral } = useReferralCode(organizationId);
  const { t } = useTranslation();
  const { mutate: generateReferralCode } =
    useGenerateReferralCode(organizationId);
  const { data: members } = useSponsoredChild(organizationId);

  return (
    <SettingContent>
      <div className={classes.header}>
        <h1 className={classes.title}>
          <Trans i18nKey="referrals.title" />
        </h1>
      </div>
      <InformationBox
        title={t('referrals.information.title')}
        text={t('referrals.information.description', { amount: 10000 })}
      />
      <div className={classes.referralCodeContainer}>
        {referral ? (
          <div>
            <LabeledSection label={t('referrals.labelLink')} color="ink">
              <SharableLink
                className={classes.link}
                link={`https://${window.location.host}/signup?code=${referral.name}`}
                copyButtonVariant="accent"
              />
            </LabeledSection>
          </div>
        ) : (
          <Button onClick={() => generateReferralCode()} color="accent">
            {t('referrals.generateReferralCode')}
          </Button>
        )}
      </div>
      {members && members.length !== 0 && (
        <div className={classes.sponsoredContainer}>
          <h2 className={classes.sponsoredTitle}>
            {t('referrals.sponsorChildTitle')}
          </h2>
          <ul className={classes.sponsoredList}>
            {members.map((member: any) => {
              return (
                <div
                  key={member.id}
                  className={classes.memberListItemWithSeparator}
                >
                  <li key={member.id} className={classes.sponsoredListItem}>
                    <div className={classes.sponsoredListItemFirst}>
                      <span className={classes.date}>
                        {new Date(member.date).toLocaleString(undefined, {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                      <span className={classes.sponsoredEmail}>
                        {member.email}
                      </span>
                    </div>
                    <Chip
                      color={member.status === 'COMPLETED' ? 'green' : 'blue'}
                    >
                      {t(
                        `referrals.refereesStatus.${member.status}`,
                      ).toUpperCase()}
                    </Chip>
                  </li>
                  <div className={classes.separator} />
                </div>
              );
            })}
          </ul>
        </div>
      )}
    </SettingContent>
  );
};
